import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Nav from "../components/Nav";
import { api, formatApiError } from "../lib/api";
import { compressImage } from "../lib/imageCompress";
import { useAuth } from "../context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function photoSrc(url) {
  if (!url) return "";
  return url.startsWith("http") ? url : `${BACKEND}${url}`;
}

function Field({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div>
      <div className="label-tag text-neutral-500">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}

export default function WineBottle() {
  const { code } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [bottle, setBottle] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    product_name: "",
    producer: "",
    category: "wine",
    vintage: "",
    variety: "",
    region: "",
    abv: "",
    volume_ml: "",
    lot: "",
    notes: "",
  });
  const [photo, setPhoto] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/public/wine/${encodeURIComponent(code)}`);
        if (cancelled) return;
        setBottle(data);
        if (data.status !== "bound") {
          setForm((f) => ({ ...f, producer: data.producer || "" }));
        }
      } catch (e) {
        if (!cancelled) setErr(formatApiError(e));
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.product_name.trim()) {
      setErr("Product name is required");
      return;
    }
    if (!photo) {
      setErr("A bottle photo is required");
      return;
    }
    setBusy(true);
    try {
      const file = await compressImage(photo);
      const body = new FormData();
      Object.entries(form).forEach(([k, v]) => body.append(k, v));
      body.append("file", file, file.name || "bottle.jpg");
      const { data } = await api.post(`/public/wine/${encodeURIComponent(code)}/register`, body);
      setBottle(data);
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Nav />
      <main className="max-w-lg mx-auto px-4 py-8">
        {err && <div className="label-tag text-red-600 mb-4" data-testid="wine-error">{err}</div>}
        {(authLoading || (!bottle && !err)) && <div className="label-tag">Loading…</div>}
        {bottle && bottle.status !== "bound" && !authLoading && !user && (
          <div className="bg-white border-2 border-black p-4" data-testid="wine-login">
            <div className="font-display font-black text-2xl">Log in to link this bottle</div>
            <p className="text-sm text-neutral-700 mt-2">Sticker #{bottle.code} is not registered yet.</p>
            <Link
              to={`/cardholder/login?next=${encodeURIComponent(`/wine/${code}`)}`}
              className="mt-4 inline-flex label-tag px-4 py-3 border-2 border-black bg-[color:var(--cc-yellow)] brutal-btn"
              data-testid="wine-login-link"
            >
              Log in
            </Link>
          </div>
        )}
        {bottle && bottle.status !== "bound" && !authLoading && user && (
          <form onSubmit={submit} className="bg-white border-2 border-black p-4 space-y-3" data-testid="wine-register">
            <div className="font-display font-black text-2xl">Register this bottle</div>
            <p className="text-sm text-neutral-700">Sticker #{bottle.code}. This can only be done once.</p>
            <label className="block">
              <span className="label-tag">Product name</span>
              <input value={form.product_name} onChange={set("product_name")} className="mt-1 w-full px-3 py-2 border border-black outline-none" data-testid="wine-product-name" />
            </label>
            <label className="block">
              <span className="label-tag">Producer</span>
              <input value={form.producer} onChange={set("producer")} className="mt-1 w-full px-3 py-2 border border-black outline-none" data-testid="wine-producer" />
            </label>
            <label className="block">
              <span className="label-tag">Category</span>
              <select value={form.category} onChange={set("category")} className="mt-1 w-full px-3 py-2 border border-black outline-none bg-white" data-testid="wine-category">
                <option value="wine">Wine</option>
                <option value="spirit">Spirit</option>
                <option value="beer">Beer</option>
              </select>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="label-tag">Vintage</span>
                <input value={form.vintage} onChange={set("vintage")} className="mt-1 w-full px-3 py-2 border border-black outline-none" data-testid="wine-vintage" />
              </label>
              <label className="block">
                <span className="label-tag">ABV</span>
                <input value={form.abv} onChange={set("abv")} placeholder="14.5" className="mt-1 w-full px-3 py-2 border border-black outline-none" data-testid="wine-abv" />
              </label>
              <label className="block">
                <span className="label-tag">Variety</span>
                <input value={form.variety} onChange={set("variety")} className="mt-1 w-full px-3 py-2 border border-black outline-none" data-testid="wine-variety" />
              </label>
              <label className="block">
                <span className="label-tag">Volume (ml)</span>
                <input value={form.volume_ml} onChange={set("volume_ml")} inputMode="numeric" className="mt-1 w-full px-3 py-2 border border-black outline-none" data-testid="wine-volume" />
              </label>
            </div>
            <label className="block">
              <span className="label-tag">Region</span>
              <input value={form.region} onChange={set("region")} className="mt-1 w-full px-3 py-2 border border-black outline-none" data-testid="wine-region" />
            </label>
            <label className="block">
              <span className="label-tag">Lot</span>
              <input value={form.lot} onChange={set("lot")} className="mt-1 w-full px-3 py-2 border border-black outline-none" data-testid="wine-lot" />
            </label>
            <label className="block">
              <span className="label-tag">Notes</span>
              <textarea value={form.notes} onChange={set("notes")} rows={3} className="mt-1 w-full px-3 py-2 border border-black outline-none" data-testid="wine-notes" />
            </label>
            <label className="block">
              <span className="label-tag">Bottle photo</span>
              <input type="file" accept="image/*" onChange={(e) => setPhoto(e.target.files?.[0] || null)} className="mt-1 block w-full text-sm" data-testid="wine-photo" />
            </label>
            <button type="submit" disabled={busy} className="bg-black text-white label-tag px-4 py-3 brutal-btn disabled:opacity-50" data-testid="wine-submit">
              {busy ? "Saving…" : "Link sticker to this bottle"}
            </button>
          </form>
        )}
        {bottle && bottle.status === "bound" && (
          <article className="bg-white border-2 border-black" data-testid="wine-bound">
            {bottle.picture_url && (
              <img src={photoSrc(bottle.picture_url)} alt={bottle.product_name} className="w-full max-h-80 object-cover border-b-2 border-black" data-testid="wine-picture" />
            )}
            <div className="p-4 space-y-3">
              <div className="label-tag">{bottle.category} · #{bottle.code}</div>
              <h1 className="font-display font-black text-3xl">{bottle.product_name}</h1>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Field label="Producer" value={bottle.producer} />
                <Field label="Vintage" value={bottle.vintage} />
                <Field label="Variety" value={bottle.variety} />
                <Field label="Region" value={bottle.region} />
                <Field label="ABV" value={bottle.abv} />
                <Field label="Volume" value={bottle.volume_ml ? `${bottle.volume_ml} ml` : ""} />
                <Field label="Lot" value={bottle.lot} />
              </div>
              {bottle.notes && <p className="text-sm whitespace-pre-wrap">{bottle.notes}</p>}
              <Link
                to={
                  !authLoading && user
                    ? `/matrix/${bottle.code}`
                    : `/cardholder/login?next=${encodeURIComponent(`/matrix/${bottle.code}`)}`
                }
                className="inline-flex label-tag px-4 py-3 border-2 border-black bg-black text-white brutal-btn"
                data-testid="wine-verify"
              >
                Verify
              </Link>
              <p className="text-sm text-neutral-600">
                Log in and use the camera on the big code. That checks it matches this small QR.
              </p>
            </div>
          </article>
        )}
      </main>
    </div>
  );
}
